import {
  Box,
  type BoxProps,
  Flex,
  HStack,
  Show,
  Stack,
  Text,
} from '@chakra-ui/react';
import { Button, Divider, Overline } from '@time-line/components/common';
import { Switzerland, VerifiedTick } from '@time-line/components/icons';
import { CredentialsCard } from 'components/common/CredentialsCard';
import { CustomerResults } from 'components/common/CustomerResults';
import { RichTextResolver } from 'components/common/RichTextResolver';
import type { AnchorBlock } from 'components/heros/AnchorCtas';
import {
  SHOP_NOW_CTA_LABEL,
  SIGN_UP_CTA_LABEL,
  SUBSCRIBE_AND_SAVE_CTA_LABEL,
} from 'components/heros/AnchorCtas/constants';
import { StickyCta } from 'components/heros/AnchorCtas/StickyCta';
import { HERO_COLORS } from 'components/heros/constants';
import { ProductFrontrowMdWidget } from 'components/product/ProductFrontrowMdWidget';
import { SubscribeWizard } from 'components/subscribe/SubscribeWizard';
import { AnchorButton } from 'components/ui/AnchorButton';
import type { PropsWithChildren } from 'react';
import type { HeroPromotionV2 } from 'types/cms/HeroPromotionV2';

interface ContentProps extends BoxProps {
  anchorBlock?: AnchorBlock;
  credentialsBullets?: HeroPromotionV2['credentialsBullets'];
  credentialsIcons?: HeroPromotionV2['credentialsIcons'];
  credentialsOverlayImage?: HeroPromotionV2['credentialsOverlayImage'];
  cta?: HeroPromotionV2['cta'];
  frontrowMDProductId?: string;
  overline: HeroPromotionV2['overline'];
  text?: HeroPromotionV2['text'];
  title: string;
}

const getAnchorCtaLabel = (anchorBlock: AnchorBlock) => {
  if (anchorBlock._type === 'timeline_signupBanner') {
    return anchorBlock.ctaTitle || SIGN_UP_CTA_LABEL;
  }

  if (
    anchorBlock._type === 'productBundle' ||
    anchorBlock._type === 'timeline_skincareFormatPicker'
  ) {
    return SHOP_NOW_CTA_LABEL;
  }

  return SUBSCRIBE_AND_SAVE_CTA_LABEL;
};

const HeroBodyText = ({ children }: PropsWithChildren) => (
  <Text size="body-serif" textAlign={{ base: 'center', lg: 'left' }}>
    {children}
  </Text>
);

export function Content({
  title,
  overline,
  text,
  cta,
  anchorBlock,
  frontrowMDProductId,
  credentialsOverlayImage,
  credentialsIcons,
  credentialsBullets,
  ...props
}: ContentProps) {
  return (
    <Stack
      h="full"
      spacing={{ base: 'sm', lg: 'lg' }}
      justify={{ lg: 'space-between' }}
      align={{ base: 'center', lg: 'flex-start' }}
      pt={{ base: 'lg', lg: 0 }}
      {...props}
    >
      <Box w="full">
        <Overline
          justifyContent={{ base: 'center', lg: 'flex-start' }}
          {...overline}
        />

        <Text
          as="h1"
          size="h2"
          textAlign={{ base: 'center', lg: 'left' }}
          mt="2xs"
        >
          {title}
        </Text>

        {text && text.length > 0 && (
          <Box mt={{ base: '3xs', lg: '2xs' }}>
            <RichTextResolver
              blocks={text}
              container={Box}
              block={{ normal: HeroBodyText }}
            />
          </Box>
        )}

        <CustomerResults
          mt="sm"
          display="flex"
          justifyContent={{ base: 'center', lg: 'flex-start' }}
          mx={{ base: 'auto', lg: 0 }}
        />

        {cta?.type === 'link' && cta.link.href && cta.link.title && (
          <Button
            as="a"
            href={cta.link.href}
            mt={{ base: '2xs', lg: 'sm' }}
            w={{ base: 'full', lg: 'auto' }}
          >
            {cta.link.title}
          </Button>
        )}

        {cta?.type === 'subscribe' && (
          <Box w="full" mt={{ base: '2xs', lg: 'sm' }}>
            <SubscribeWizard
              buttonColorScheme="gray"
              colorScheme="gray"
              consentType={cta.consentCapture?.variant || 'email'}
              autoSubscribeMarketing={
                cta.consentCapture?.autoSubscribeMarketing
              }
              klaviyoListId={cta.klaviyoListId}
            />
          </Box>
        )}

        {cta?.type === 'anchor' && anchorBlock && (
          <AnchorButton
            anchorId={anchorBlock._key}
            w={{ base: 'full', lg: '260px' }}
            mt={{ base: '2xs', lg: 'sm' }}
          >
            {getAnchorCtaLabel(anchorBlock)}
          </AnchorButton>
        )}

        {cta?.type === 'link' && cta.link.href && cta.link.title && (
          <StickyCta
            type="link"
            href={cta.link.href}
            label={cta.link.title}
            showCart
          />
        )}

        {cta?.type === 'anchor' && anchorBlock && (
          <StickyCta
            type="anchor"
            anchorId={anchorBlock._key}
            isSubscription={
              anchorBlock._type !== 'productBundle' &&
              anchorBlock._type !== 'timeline_skincareFormatPicker' &&
              anchorBlock._type !== 'timeline_signupBanner'
            }
            label={
              anchorBlock._type === 'timeline_signupBanner'
                ? anchorBlock.ctaTitle || SIGN_UP_CTA_LABEL
                : undefined
            }
            showCart
          />
        )}

        <Flex
          mt={{ base: '2xs', lg: 'sm' }}
          direction={{ base: 'column', '2xl': 'row' }}
          align={{ base: 'center', lg: 'flex-start', '2xl': 'center' }}
          gap={{ base: '4xs', '2xl': '5xs' }}
        >
          <HStack spacing="4xs">
            <VerifiedTick width="2xs" height="2xs" />
            <Text size="detail" fontWeight="medium">
              30 day money-back guarantee
            </Text>
          </HStack>
          <Text size="detail" color={HERO_COLORS.gray.gentle}>
            <Box as="span" display={{ base: 'none', '2xl': 'inline' }}>
              &middot;{' '}
            </Box>
            Free US Shipping &middot; Pause or cancel at any time
          </Text>
        </Flex>
      </Box>

      {frontrowMDProductId && (
        <Show below="lg">
          <Box w="full">
            <Divider w="full" mb="sm" />
            <ProductFrontrowMdWidget id={frontrowMDProductId} variant="quant" />
          </Box>
        </Show>
      )}

      <Stack w="full" spacing="xs" align={{ base: 'center', lg: 'flex-start' }}>
        <CredentialsCard
          overlayImage={credentialsOverlayImage}
          icons={credentialsIcons}
          bullets={credentialsBullets}
        />

        <Flex align="center" gap="4xs">
          <Switzerland width="18px" height="18px" />
          <Text size="title3">Formulated in Switzerland</Text>
        </Flex>
      </Stack>
    </Stack>
  );
}
